import { AlgoliaMedia } from '@omniafishing/core';
import React from 'react';
import { useFrontload } from 'react-frontload';
import { mediaDateSortedIndex, mediaIndex } from '../../algolia';
import { LAKE_HERO_TAG } from '../../components/content_list/content_list';
import { useAfterMountedEffect } from '../../hooks/use_after_mounted_effect';
import { useQueryString } from '../../hooks/use_query_string';
import { useUser } from '../../hooks/use_user';
import { MediaList, MediaListQueryParams } from './media_list';

export const CONTENT_LIST_PER_PAGE_DEFAULT = 48;

export enum MediaFacets {
  AMBASSADORS = 'ambassadors.full_name',
  BRANDS = 'brands.display_name',
  CREATOR = 'creator.full_name',
  SPECIES = 'species.display_name',
  STATES = 'states.name',
  TECHNIQUES = 'techniques.display_name',
  WATERBODIES = 'waterbodies.primary_name',
}

export const MEDIA_PAGE_SIZE_DEFAULT = 24;

export const MediaListContainer = () => {
  const { user } = useUser();
  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<MediaListQueryParams>();
  const { q, pg, species, techniques, brands, ambassadors, states } = currentQuery;

  const page = Math.max(Number(pg || 1) - 1, 0);
  const queryTechniques = techniques || [];
  const techniquesFilters = queryTechniques.map((t) => `${MediaFacets.TECHNIQUES}:"${t}"`);
  const queryBrands = brands || [];
  const brandsFilters = queryBrands.map((b) => `${MediaFacets.BRANDS}:"${b}"`);
  const queryAmbassadors = ambassadors || [];
  const ambassadorsFilters = queryAmbassadors.map((a) => `${MediaFacets.AMBASSADORS}:"${a}"`);
  const queryStates = states || [];
  const statesFilters = queryStates.map((s) => `${MediaFacets.STATES}:"${s}"`);
  const querySpecies = species || [];
  const speciesFilters = querySpecies.map((sp) => `${MediaFacets.SPECIES}:"${sp}"`);

  // These filter strings need to use double quote because some facet values include single quote
  const notLakeTagFilter = ` NOT tags:"${LAKE_HERO_TAG}"`;
  let filters = [
    ...speciesFilters,
    ...techniquesFilters,
    ...brandsFilters,
    ...ambassadorsFilters,
    ...statesFilters,
  ].join(' OR ');
  if (filters.length) {
    filters += ` AND`;
  }
  filters += notLakeTagFilter;

  const allFacets = [
    MediaFacets.AMBASSADORS,
    MediaFacets.BRANDS,
    MediaFacets.CREATOR,
    MediaFacets.SPECIES,
    MediaFacets.STATES,
    MediaFacets.TECHNIQUES,
    MediaFacets.WATERBODIES,
  ];

  const searchOptions = {
    facets: allFacets,
    filters,
    page,
    hitsPerPage: 24,
    clickAnalytics: true,
    enablePersonalization: true,
    userToken: user?.id?.toString(),
  };

  const getSearchResults = () => {
    const index = q ? mediaIndex : mediaDateSortedIndex;
    return index.search<AlgoliaMedia>(q, searchOptions);
  };

  const getSearchFacets = () => {
    const index = q ? mediaIndex : mediaDateSortedIndex;
    return index.search<AlgoliaMedia>(q, {
      ...searchOptions,
      filters: notLakeTagFilter,
    });
  };

  const { data, setData } = useFrontload('MediaListContainer', async () => {
    const searchResults = await getSearchResults();
    const facetResults = await getSearchFacets();

    const { hits, nbHits, nbPages } = searchResults;
    const { facets } = facetResults;

    return {
      hits,
      facets,
      nbHits,
      nbPages,
    };
  });

  useAfterMountedEffect(() => {
    getSearchResults().then((res) => {
      const { hits, nbHits, nbPages } = res;
      setData((currentData) => ({
        ...currentData,
        hits,
        nbHits,
        nbPages,
      }));
    });
  }, [page, q, filters]);

  useAfterMountedEffect(() => {
    getSearchFacets().then((res) => {
      const { facets } = res;
      setData((currentData) => ({
        ...currentData,
        facets,
      }));
    });
  }, [q]);

  return (
    <MediaList
      media={data?.hits || []}
      facets={data?.facets || {}}
      mediaTotalCount={data?.nbHits || 0}
      page={page + 1}
    />
  );
};
